import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Facebook, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { postNewsletter } from '../lib/api';

export default function Footer() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', hp: '' });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.hp) return; // honeypot triggered — silently ignore
    if (!form.email) return;
    setLoading(true);
    try {
      await postNewsletter({
        email: form.email.trim(),
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
      });
      toast.success('Thank you. You are subscribed to The Green Room.');
      setForm({ first_name: '', last_name: '', email: '', hp: '' });
    } catch {
      toast.error('Sorry, we could not complete your subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#001a38] text-white">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-24 pb-10">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 min-w-0">
            <p className="eyebrow text-[#C5A059] whitespace-normal">David Ball Management · London</p>
            <p className="mt-6 max-w-md text-white/75 text-sm leading-relaxed">
              A London agency representing actors across television, film,
              theatre, commercials and voice work.
            </p>

            <form onSubmit={submit} data-testid="footer-newsletter-form" className="mt-10 max-w-md">
              <p className="eyebrow text-white/70">The Green Room · Newsletter</p>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="border-b border-white/25 focus-within:border-[#C5A059] transition-colors">
                  <input
                    data-testid="footer-newsletter-fname"
                    type="text"
                    autoComplete="given-name"
                    aria-label="First name"
                    placeholder="First name"
                    value={form.first_name}
                    onChange={upd('first_name')}
                    className="w-full bg-transparent py-2.5 outline-none text-white placeholder:text-white/40 text-sm"
                  />
                </div>
                <div className="border-b border-white/25 focus-within:border-[#C5A059] transition-colors">
                  <input
                    data-testid="footer-newsletter-lname"
                    type="text"
                    autoComplete="family-name"
                    aria-label="Last name"
                    placeholder="Last name"
                    value={form.last_name}
                    onChange={upd('last_name')}
                    className="w-full bg-transparent py-2.5 outline-none text-white placeholder:text-white/40 text-sm"
                  />
                </div>
              </div>

              <div className="mt-4 flex items-center border-b border-white/25 focus-within:border-[#C5A059] transition-colors">
                <input
                  data-testid="footer-newsletter-input"
                  type="email"
                  required
                  autoComplete="email"
                  aria-label="Email address"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={upd('email')}
                  className="bg-transparent flex-1 min-w-0 py-3 outline-none text-white placeholder:text-white/40 text-sm"
                />
                <button
                  data-testid="footer-newsletter-submit"
                  type="submit"
                  disabled={loading}
                  className="ml-3 text-[#C5A059] text-xs tracking-[0.25em] uppercase hover:text-white transition-colors disabled:opacity-50"
                >
                  {loading ? 'Sending…' : 'Subscribe'}
                </button>
              </div>

              <p className="mt-3 text-[11px] text-white/50 leading-relaxed">
                By subscribing you consent to receive occasional emails from David Ball Management.
                Unsubscribe any time. See our{' '}
                <Link to="/privacy" className="underline decoration-white/30 hover:text-white/80">privacy notice</Link>.
              </p>

              {/* honeypot (hidden from users; catches basic bots) */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.hp}
                onChange={upd('hp')}
                className="sr-only"
              />
            </form>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow text-white/70">Navigate</p>
            <ul className="mt-6 space-y-3 text-sm text-white/80">
              {[
                ['/about', 'About'],
                ['/clients', 'Clients'],
                ['/creative-management', 'Creative Management'],
                ['/green-room', 'The Green Room'],
                ['/news', 'News'],
                ['/contact', 'Contact'],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="hover:text-[#C5A059] transition-colors gold-underline">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="eyebrow text-white/70">Contact</p>
            <address className="mt-6 not-italic text-sm text-white/80 leading-relaxed">
              29 Holmdale Road<br />
              London, BR7 6BY<br />
              United Kingdom
            </address>
            <div className="mt-6 space-y-2 text-sm text-white/80">
              <p><a href="tel:+442033550561" data-testid="footer-phone" className="hover:text-[#C5A059] gold-underline">+44 (0) 203 355 0561</a></p>
              <p><a href="mailto:office@dbmtalent.com" data-testid="footer-email" className="hover:text-[#C5A059] gold-underline">office@dbmtalent.com</a></p>
            </div>

            <div className="mt-8 flex items-center gap-5">
              <a data-testid="social-instagram" href="https://www.instagram.com/dbmtalent/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-white/70 hover:text-[#C5A059] transition-colors"><Instagram size={18} /></a>
              <a data-testid="social-linkedin" href="https://www.linkedin.com/company/dbm-talent/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-white/70 hover:text-[#C5A059] transition-colors"><Linkedin size={18} /></a>
              <a data-testid="social-facebook" href="https://www.facebook.com/davidballmanagement/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white/70 hover:text-[#C5A059] transition-colors"><Facebook size={18} /></a>
            </div>
          </div>
        </div>

        <div className="hairline mt-20 opacity-40" />

        <div className="mt-8 flex flex-col md:flex-row justify-between gap-4 text-[11px] tracking-[0.2em] uppercase text-white/50">
          <p>© {new Date().getFullYear()} David Ball Limited. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#C5A059] transition-colors">Privacy &amp; Cookies</Link>
            <span className="hidden md:inline text-white/30">·</span>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[#C5A059] transition-colors">Terms of Use</Link>
            <span className="hidden md:inline text-white/30">·</span>
            <p className="flex items-center gap-2">
              Registered in England &amp; Wales <ArrowUpRight size={12} />
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
